import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Download, FileText, Zap, Activity, TrendingUp, Cpu, FileBarChart2, Lock, Wallet, RotateCw, Store } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart,
} from "recharts";
import { RiskNote } from "../components/layout/RiskDisclaimer";
import ContractsWidget from "../components/dashboard/ContractsWidget";

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [invs, setInvs] = useState([]);
  const [dists, setDists] = useState([]);
  const [spvs, setSpvs] = useState([]);
  const [live, setLive] = useState(null);
  const [liveHistory, setLiveHistory] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [s, i, d, sp] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/investments"),
        api.get("/distributions"),
        api.get("/spvs"),
      ]);
      if (!active) return;
      setSummary(s.data); setInvs(i.data); setDists(d.data); setSpvs(sp.data);
    };
    load().catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/gpu/live");
        if (!mounted) return;
        setLive(data);
        setLiveHistory((prev) => {
          const point = { t: new Date(data.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), occupancy: data.occupancy_pct, power: data.power_kw };
          const next = [...prev, point].slice(-20);
          return next;
        });
      } catch {}
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const distByMonth = (() => {
    const m = {};
    dists.forEach((d) => {
      const key = (d.date || "").slice(0, 7);
      m[key] = (m[key] || 0) + d.amount;
    });
    return Object.entries(m)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([k, v]) => ({ month: k.slice(5), value: Math.round(v) }));
  })();

  const spvMap = Object.fromEntries(spvs.map((s) => [s.spv_id, s]));

  const statusLabel = (st) => ({
    pending: lang === "fr" ? "En attente" : "Pending",
    compliance_review: lang === "fr" ? "Conformité" : "Compliance",
    approved: lang === "fr" ? "Approuvé" : "Approved",
    funded: lang === "fr" ? "Investi" : "Funded",
    rejected: lang === "fr" ? "Rejeté" : "Rejected",
  }[st] || st);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="label-divider mb-3">{t.nav.dashboard}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900" data-testid="dashboard-welcome">
            {t.dashboard.welcome}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {user?.kyc_status === "qualified" ? (lang === "fr" ? "Profil qualifié" : "Qualified profile") : (lang === "fr" ? "Profil en attente de qualification" : "Profile pending qualification")}
          </p>
        </div>
        <div className="flex gap-2">
          {user?.kyc_status !== "qualified" && (
            <Button asChild variant="outline" className="rounded-sm" data-testid="dashboard-cta-qualif">
              <Link to="/qualification">{lang === "fr" ? "Compléter la qualification" : "Complete qualification"}</Link>
            </Button>
          )}
          <Button asChild variant="outline" className="rounded-sm" data-testid="dashboard-cta-dataroom">
            <Link to="/data-room">{lang === "fr" ? "Data Room" : "Data Room"}</Link>
          </Button>
          <Button asChild className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="dashboard-cta-invest">
            <Link to="/invest">{t.dashboard.subscribe_new} <ArrowRight className="h-4 w-4 ml-2" /></Link>
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden" data-testid="dashboard-kpis">
        <KPI label={t.dashboard.value_invested} value={fmt(summary?.total_invested)} sub={`${summary?.active_spvs || 0} ${t.dashboard.active_spvs}`} testid="kpi-invested" />
        <KPI label={t.dashboard.total_distributed} value={fmt(summary?.total_distributed)} sub={lang === "fr" ? "capitalisé depuis origine" : "capitalised since inception"} testid="kpi-distributed" />
        <KPI label={t.dashboard.target_yield} value={summary ? `${summary.target_yield_pct}%` : "—"} sub={lang === "fr" ? "scénario central" : "central scenario"} testid="kpi-yield" />
        <KPI label={t.dashboard.next_distribution} value={fmt((summary?.next_distribution_eur || 0) * 6)} sub={lang === "fr" ? "estimation semestrielle" : "semi-annual estimate"} testid="kpi-next" />
      </div>

      {/* Capitalisation account widget */}
      <CapitalisationAccount summary={summary} lang={lang} t={t} />

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        {/* Revenues chart */}
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-sm p-6" data-testid="revenues-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="label-divider !text-slate-800">{t.dashboard.total_distributed}</div>
            <TrendingUp className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distByMonth}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} stroke="#64748B" fontSize={11} />
                <YAxis tickLine={false} axisLine={false} stroke="#64748B" fontSize={11} tickFormatter={(v) => `${v}€`} />
                <Tooltip contentStyle={{ borderRadius: 2, borderColor: "#E2E8F0", fontSize: 12 }} formatter={(v) => `${v}€`} />
                <Bar dataKey="value" fill="#059669" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* GPU live */}
        <div className="lg:col-span-5 bg-slate-900 text-white rounded-sm p-6" data-testid="gpu-live-widget">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-bold flex items-center gap-2">
              <span className="live-dot" /> {t.dashboard.gpu_live}
            </div>
            <Cpu className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <LiveStat k={t.dashboard.occupancy} v={live ? `${live.occupancy_pct}%` : "—"} />
            <LiveStat k={t.dashboard.uptime} v={live ? `${live.uptime_pct}%` : "—"} />
            <LiveStat k={t.dashboard.gpus_active} v={live ? `${live.gpus_active} / ${live.gpus_total}` : "—"} />
            <LiveStat k={t.dashboard.power} v={live ? `${live.power_kw} kW` : "—"} />
            <LiveStat k={t.dashboard.daily_revenue} v={live ? `${live.daily_revenue_eur.toLocaleString()}€` : "—"} />
            <LiveStat k={t.dashboard.co2_saved} v={live ? `${live.co2_saved_kg} kg` : "—"} />
          </div>
          <div className="mt-6 h-24">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={liveHistory}>
                <defs>
                  <linearGradient id="g-occ" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" hide />
                <YAxis domain={[60, 100]} hide />
                <Area type="monotone" dataKey="occupancy" stroke="#10B981" strokeWidth={2} fill="url(#g-occ)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Performance table + documents */}
      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-sm p-6" data-testid="performance-widget">
          <div className="label-divider mb-5 !text-slate-800">{t.dashboard.performance}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200">
                  <th className="py-3 pr-4 font-medium">SPV</th>
                  <th className="py-3 pr-4 font-medium">{lang === "fr" ? "Montant" : "Amount"}</th>
                  <th className="py-3 pr-4 font-medium">{lang === "fr" ? "Statut" : "Status"}</th>
                  <th className="py-3 pr-4 font-medium">{lang === "fr" ? "Cible" : "Target"}</th>
                  <th className="py-3 pr-4 font-medium">{lang === "fr" ? "Date" : "Date"}</th>
                </tr>
              </thead>
              <tbody>
                {invs.length === 0 && (
                  <tr><td colSpan={5} className="py-10 text-center text-slate-500">
                    {lang === "fr" ? "Aucun investissement pour le moment." : "No investments yet."}
                  </td></tr>
                )}
                {invs.map((i) => (
                  <tr key={i.investment_id} className="border-b border-slate-100" data-testid={`inv-row-${i.investment_id}`}>
                    <td className="py-4 pr-4">
                      <div className="font-medium text-slate-900">{spvMap[i.spv_id]?.name || i.spv_id}</div>
                      <div className="text-xs text-slate-500">{spvMap[i.spv_id]?.hardware}</div>
                    </td>
                    <td className="py-4 pr-4 font-mono">{fmt(i.amount)}</td>
                    <td className="py-4 pr-4">
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-sm ${
                        i.status === "funded" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" :
                        i.status === "approved" ? "bg-slate-100 text-slate-800 border border-slate-200" :
                        i.status === "compliance_review" ? "bg-amber-50 text-amber-800 border border-amber-200" :
                        i.status === "rejected" ? "bg-red-50 text-red-800 border border-red-200" :
                        "bg-slate-100 text-slate-600 border border-slate-200"
                      }`}>{statusLabel(i.status)}</span>
                    </td>
                    <td className="py-4 pr-4 font-mono text-slate-700">{spvMap[i.spv_id]?.target_yield || "—"}%</td>
                    <td className="py-4 pr-4 text-slate-500 text-xs">{(i.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:col-span-4 bg-white border border-slate-200 rounded-sm p-6" data-testid="documents-widget">
          <div className="flex items-center justify-between mb-5">
            <div className="label-divider !text-slate-800">{lang === "fr" ? "Reportings mensuels" : "Monthly reports"}</div>
            <Link to="/dashboard/reports" className="text-xs text-slate-700 hover:text-slate-900 flex items-center gap-1" data-testid="dashboard-reports-link">
              {lang === "fr" ? "Tous" : "All"} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {["Reporting Octobre 2025", "Reporting Novembre 2025", "Reporting Décembre 2025", "Reporting Janvier 2026"].map((d, i) => (
              <li key={i} className="flex items-center justify-between py-3" data-testid={`doc-${i}`}>
                <div className="flex items-center gap-3">
                  <FileBarChart2 className="h-4 w-4 text-emerald-700" />
                  <span className="text-sm text-slate-800">{d}</span>
                </div>
                <Link to="/dashboard/reports" className="text-xs text-slate-500 hover:text-slate-900">
                  {lang === "fr" ? "Voir" : "View"}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Contracts widget */}
      <div className="mt-8">
        <ContractsWidget />
      </div>

      <div className="mt-10"><RiskNote /></div>
    </div>
  );
}

function KPI({ label, value, sub, testid }) {
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className="mt-2 font-mono text-2xl font-semibold text-slate-900 counter-in" key={value}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function LiveStat({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{k}</div>
      <div className="mt-1 font-mono text-lg font-semibold text-white">{v}</div>
    </div>
  );
}

function CapitalisationAccount({ summary, lang, t }) {
  const balance = summary?.total_distributed || 0;
  const nextCoupon = (summary?.next_distribution_eur || 0) * 6;
  // Rough next-coupon date: next 30 June or 31 December
  const nextDate = (() => {
    const now = new Date();
    const y = now.getFullYear();
    const june = new Date(y, 5, 30);
    const december = new Date(y, 11, 31);
    const target = now <= june ? june : (now <= december ? december : new Date(y + 1, 5, 30));
    return target.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "long", year: "numeric" });
  })();

  return (
    <section className="mt-8 bg-slate-900 text-white rounded-sm overflow-hidden" data-testid="capitalisation-account">
      <div className="grid md:grid-cols-12">
        <div className="md:col-span-7 p-7 lg:p-9 border-b md:border-b-0 md:border-r border-slate-800">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-bold">
            <Wallet className="h-3.5 w-3.5" />
            <span>{t.dashboard.coupon_account_title}</span>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed max-w-xl">
            {t.dashboard.coupon_account_desc}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild className="rounded-sm bg-emerald-600 hover:bg-emerald-500 h-9 text-xs" data-testid="coupon-cta-reinvest">
              <Link to="/invest">
                <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                {t.dashboard.coupon_action_reinvest}
              </Link>
            </Button>
            <Button disabled className="rounded-sm bg-slate-700 text-slate-300 hover:bg-slate-700 h-9 text-xs cursor-not-allowed" data-testid="coupon-cta-marketplace">
              <Store className="h-3.5 w-3.5 mr-1.5" />
              {t.dashboard.coupon_action_marketplace}
            </Button>
          </div>
        </div>
        <div className="md:col-span-5 p-7 lg:p-9 grid grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">{t.dashboard.coupon_balance}</div>
            <div className="mt-2 font-mono text-3xl font-semibold text-white counter-in" key={balance} data-testid="coupon-balance">
              {fmt(balance)}
            </div>
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-300">
              <Lock className="h-2.5 w-2.5" />
              {t.dashboard.coupon_locked_label}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">{t.dashboard.coupon_next}</div>
            <div className="mt-2 font-mono text-3xl font-semibold text-white" data-testid="coupon-next">
              {fmt(nextCoupon)}
            </div>
            <div className="mt-1 text-[10px] uppercase tracking-[0.15em] text-slate-400">
              {t.dashboard.coupon_next_date} : {nextDate}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
